<template>
	<ClickoutAnchor
		v-if="promotion && offer"
		class="content-link"
		:href="clickOutUrl"
		@click="handleClickout"
		clickoutType="promoted"
		v-observe-visibility="observeVisibilityOptions"
	>
		<div class="play-btn">
			<svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path
					fill="#FFD22E"
					d="M13.2625 6.70837L2.2625 0.205248C1.36875 -0.322877 0 0.189623 0 1.49587V14.499C0 15.6709 1.27188 16.3771 2.2625 15.7896L13.2625 9.28962C14.2438 8.7115 14.2469 7.2865 13.2625 6.70837Z"
				/>
			</svg>
		</div>

		<div class="offer-wrapper">
			<ProviderIcon :iconUrl="promotion.icon" :lazy="false" :providerShortName="promotion.provider" />

			<div class="content">
				<p class="label">{{ $t('WEBAPP_WATCHNOW') }}</p>
				<span class="sub-label" v-if="promotion">
					{{ promotion.longText }}
					<DisclaimerButton v-if="showDisclaimerButton" :disclaimerText="promotion.disclaimerText" />
				</span>
			</div>
		</div>
	</ClickoutAnchor>
</template>

<script setup lang="ts">
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { ClickoutUrl } from '@/helpers/clickout-helper';

import { OfferLocation } from '@/helpers/offer-helper';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { computed } from 'vue';
import { ObjectType } from '@/@types/graphql-types';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { isTitleSeason } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { useProviderPromotion } from '@/helpers/provider-promotion';
import DisclaimerButton from '@/features/buybox/components/DisclaimerButton.vue';

import { useAppStore } from '@/stores/pinia/useAppStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';
import { usePrimePlayPromoExp } from '@/components/experiments/PrimePlayPromoExp';

const props = withDefaults(defineProps<{ title: TitleDetail; additionalContexts?: SnowplowContext[] }>(), {
	additionalContexts: () => [],
});

const { country } = storeToRefs(useLanguageStore());
const { xServeTrack } = storeToRefs(useAppStore());

const { providerPromotion: promotion, isPromotionOffer } = useProviderPromotion(() => props.title);

// we can be reasonably sure there is an offer because the parent would check
// nonetheless, there's an extra check in the template
const offer = computed(() => props.title.offers.find(offer => isPromotionOffer(offer)));

const showDisclaimerButton = computed(() => promotion.value?.disclaimerText != null);

const titleObjectId = computed(() => (isTitleSeason(props.title) ? props.title.show?.objectId : props.title.objectId));
const titleObjectType = computed(() => (isTitleSeason(props.title) ? ObjectType.Show : props.title.objectType));

const titleContext = computed(() => new SnowplowTitleContextGraphql(titleObjectId.value, titleObjectType.value));

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

const { setPrimePlayPromoTags } = usePrimePlayPromoExp();

const clickOutUrl = computed(() => {
	const clickoutOffer = promotion.value?.url ? { ...offer.value, standardWebURL: promotion.value.url } : offer.value;
	const skipRedirect = !!promotion.value?.url;
	const isATVPlus = offer.value?.package?.packageId === 350;

	const url = new ClickoutUrl(clickoutOffer)
		.setTitleSourceId(props.title.id)
		.skipYieldOptimization(isATVPlus)
		.skipRedirect(skipRedirect)
		.addContexts(titleContext.value)
		.set('uct_country', country.value)
		.setForTracking('offerLocation', OfferLocation.BACKDROP)
		.updateContext('ClickoutContextGraphql', 'clickoutType', 'promoted')
		.updateContext('ClickoutContextGraphql', 'placement', 'backdrop')
		.updateContext('TitleContextGraphql', 'jwRawEntityId', props.title.id)
		.setCanary(xServeTrack.value);

	url.apply(u =>
		setPrimePlayPromoTags(u, 'play', offer.value?.package.shortName, offer.value?.package.technicalName)
	);

	if (['dnp', 'hst'].includes(offer.value?.package?.shortName || '')) {
		const tag = `disney-${country.value.toLowerCase()}-promo`;
		url.set('uct_ctag', tag);
	}

	/**
	 * BINGE - note binge UCT2 rules are for a specific campagin - the next one may need a url update
	 */

	return url;
});

const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

function onVisibilityChanged(isVisible: boolean) {
	if (!isVisible) return;

	onIntersection(clickOutUrl.value, isVisible);
}

function handleClickout() {
	if (offer.value == null) return;
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/rtl-support-mixins';

.content-link {
	text-decoration: none;
	color: unset;
	background: $jw-color-primary-dark;
	border-radius: 24px;
	display: flex;
	padding: 8px;
	align-items: center;
	max-width: 400px;
	transform: translateY(-8px);

	.play-btn {
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;

		background-color: #060d17;
		border-radius: 100%;

		width: 48px;
		min-width: 48px;
		height: 48px;
		min-height: 48px;
		z-index: 100;
		border: 1px solid rgba(198, 200, 205, 0.6);
		margin-right: -24px;

		svg {
			width: 16px;
		}
	}
	.offer-wrapper {
		background: $jw-color-primary-light;
		background-color: #232c37;
		border-radius: 16px;
		padding: 8px;
		padding-inline-start: 32px;
		display: flex;
		gap: 8px;
		align-items: center;

		.content {
			p {
				margin: 0;
			}
			.label {
				color: $jw-color-gold;
				text-transform: uppercase;
				margin-bottom: 4px;
				font-size: 16px;
				line-height: 24px;
			}

			.sub-label {
				font-size: 14px;
				line-height: 16px;
				color: white;
			}
		}
	}
}
</style>
